import React, { useCallback, useEffect, useLayoutEffect } from 'react';
import { Box, Grid, Button, MenuItem, FormControl, InputLabel, Select, ListSubheader } from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { addLoadingAction, removeLoadingAction } from 'store/actions/loadingsActions';
import FilterPanel from 'components/DashboardComponents/FilterPanel';
import useApi from 'hooks/useApi';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import { SelectedFiltersList } from 'types/models/SelectedFiltersList';
import KpiMetricsWorkflow from './KpiMetricsTabPages/KpiMetricsWorkflow';
import KpiMetricsAcceptance from './KpiMetricsTabPages/KpiMetricsAcceptance';
import KpiMetricsPerformance from './KpiMetricsTabPages/KpiMetricsPerformance';
import KpiMetricsCoverRatio from './KpiMetricsTabPages/KpiMetricsCoverRatio';
import DashboardTabServices from './hooks/DashboardTabServices';
import Loader from 'components/Loader';
import transformPendingData from './helpers/transformPendingData';
import aggregateDonutData from './helpers/aggregateDonutData';
import { KpiMetricTableType } from 'types/enums/KpiMetricTableType';
import { selectFilterRemoveAction } from 'store/actions/filterActions';
import { sortKpimetricGridAction } from 'store/actions/dashboardActions';

// --------- SAME DATA AS YOUR CODE (unchanged) ----------

type Selected = { [key: string]: string | string[] };
type FormattedSelected = { [key: string]: string };

type Metric = { id: number; page_title: string };

type Order = { mission_advancement_id: number; count: number };
type OrdersShape = {
  consultant_submission: Order[];
  dm_validation: Order[];
  client_acceptance: Order[];
  acceptance_data: any;
  coverRatioData: Order[];
  otpCustomerData: Order[];
  otpBMData: Order[];
};

interface KpiMetricsTabProps {
  pendingStartDate: string | undefined;
  pendingEndDate: string | undefined;
  setPendingStartDate: React.Dispatch<React.SetStateAction<string | undefined>>;
  setPendingEndDate: React.Dispatch<React.SetStateAction<string | undefined>>;
  metric: Metric | null;
  setMetric: any;
  // sortModel: GridSortModel;
  // setSortModel: React.Dispatch<React.SetStateAction<GridSortModel>>;
  metricPage: readonly Metric[];
}

type GraphData = {
  orders: Partial<OrdersShape>; // This allows metric 2 and 4 to only return what they need
  Message?: string;
};

// Default date range used whenever navigation rules require resetting the filter dates
const DEFAULT_START_DATE = moment().subtract(6, 'months').startOf('month').format('YYYY-MM-DD');
const DEFAULT_END_DATE = moment().format('YYYY-MM-DD');

const getInitialGraphData = (metricId?: Metric): GraphData => {
  if (metricId?.page_title === KpiMetricTableType.ACCEPTANCE) {
    return {
      orders: {
        consultant_submission: [],
        dm_validation: [],
        client_acceptance: [],
        acceptance_data: [],
      },
    };
  }

  if (metricId?.page_title === KpiMetricTableType.COVER_RATIO) {
    return {
      orders: {
        coverRatioData: [],
        otpCustomerData: [],
        otpBMData: [],
      },
    };
  }

  // Default for metric 1
  return {
    orders: {
      consultant_submission: [],
      dm_validation: [],
      client_acceptance: [],
    },
  };
};

const KpiMetricsTab: React.FC<KpiMetricsTabProps> = ({
  pendingStartDate,
  pendingEndDate,
  setPendingStartDate,
  setPendingEndDate,
  metric,
  setMetric,
  // setSortModel,
  // sortModel,
  metricPage,
}) => {
  const { makeCall } = useApi();
  const [t] = useTranslation();
  const { dispatch } = useAppState();
  const { loadings } = useSelector((state) => state);
  const { filter, dashboard } = useSelector((state) => state);
  const [pageSize, setPageSize] = React.useState(10);
  const [currentPage, setCurrentPage] = React.useState(0);
  const [showFilterPanel, setShowFilterPanel] = React.useState(true);
  const [_, setForceUpdate] = React.useState(0); // State to force re-render
  // This triggers the change whenever metricId updates
  const isInitialKpiFetch = React.useRef(true);
  const isFirstLoadEffect1 = React.useRef(true);
  const initialMetricId = metricPage[0]?.id || 1;

  //  const [sortModel, setSortModel] = React.useState<GridSortModel>([{ field: 'id', sort: 'asc' }]);
  const [metricsList, setMetricsList] = React.useState<Metric[]>([]);
  const [filterValues, setFilterValues] = React.useState();
  const [displaySelectedCountry, setDisplaySelectedCountry] = React.useState<string>('');
  const [displaySelectedStatus, setDisplaySelectedStatus] = React.useState<string>('');
  const [currencies, setCurrencies] = React.useState();
  const [selectedCurrency, setSelectedCurrency] = React.useState('Euro');

  // React.useEffect(() => {
  //   if (currencies && currencies.length > 0) {
  //     const hasEuro = currencies.find(c => c.name.toLowerCase() === 'euro');
  //     // Set to Euro if found, otherwise default to the very first item in the list
  //     setSelectedCurrency(hasEuro ? hasEuro.name : currencies[1].name);
  //   }
  // }, [currencies]);

  // Handler for date changes in filter panel
  const handleDateChange = () => {
    setForceUpdate((prev) => prev + 1); // Force re-render
  };

  const [graphData, setGraphData] = React.useState<GraphData>(() => getInitialGraphData());
  const [barChartData, setBarChartData] = React.useState<any[]>([]);
  const [gridData, setGridData] = React.useState<any[]>([]);
  const targetIds = [metricPage?.[4]?.id, metricPage?.[5]?.id, metricPage?.[6]?.id]; // Financial pages (5, 6, 7)
  const DELIVERY_METRIC_IDS = [metricPage?.[0]?.id, metricPage?.[1]?.id, metricPage?.[2]?.id]; // Delivery pages (1, 2, 3)
  const COVER_RATIO_METRIC_ID = metricPage?.[3]?.id; // Cover Ratio page (4)

  const { fetchKpiMetricGraph, getKpiMetricSuperFilter, fetchKpiMetricGrid, downloadKpiGridExcel } =
    DashboardTabServices();
  // Reset graph data when metric changes
  useEffect(() => {
    if (metric?.id) {
      setGraphData(getInitialGraphData(metric));
    }
  }, [metric?.id]);

  const fetchKpiFilter = async () => {
    const params = await formatSelected(filter.selected);
    if (metric?.id && targetIds.includes(metric.id)) {
      params.currency = selectedCurrency;
    }
    try {
      dispatch(addLoadingAction('dashboardSuperFilter'));
      // Set flag to true right before changing the metric state
      isInitialKpiFetch.current = true;
      const response = await getKpiMetricSuperFilter(metric?.id || 1, params);
      if (response && response.metrics) {
        setMetricsList(response.metrics);
        if (!metric && response.metrics[0]) {
          setMetric(response.metrics[0]);
        }
        if (metric?.id && targetIds.includes(metric.id)) {
          setCurrencies(response.currencies);
        }
        if (metric?.id && metric?.id < 6) {
          fetchDonutGraph();
        }
        fetchDonutGrid();
      }
    } catch (error) {
      console.error('Failed to fetch Super filter:', error);
      // Reset flag if the API fails so subsequent changes work
      isInitialKpiFetch.current = false;
    } finally {
      dispatch(removeLoadingAction('dashboardSuperFilter'));
    }
  };

  useEffect(() => {
    fetchKpiFilter();
  }, [_]);

  async function formatSelected(selected: Selected): Promise<SelectedFiltersList> {
    const output: SelectedFiltersList = {};

    const formatValue = (value: string | string[]): string | null => {
      if (Array.isArray(value)) {
        const cleanArray = value.filter((v) => v !== undefined && v !== null && v !== '');

        if (cleanArray.length === 0) return null;

        // FIX: Return null instead of 'all' so the key is removed from the output
        if (cleanArray.length > 1 && cleanArray[0] === 'ALL') return null;

        return cleanArray.length === 0 ? cleanArray[0] : cleanArray.sort().join(',');
      }

      // Optional: Handle cases where a single string "ALL" is passed
      if (value === 'ALL' || !value || value.trim() === '') return null;
      return value;
    };

    Object.entries(selected).forEach(([key, val]) => {
      if (!key || key === 'undefined' || key === 'null') return;
      if (val === undefined || val === null) return;

      const formatted = formatValue(val);

      // Remove the key-value pair when the formatted value is 'ALL'.
      if (formatted !== null && formatted !== 'ALL') {
        output[key] = formatted;
      }
    });
    const result_output = {
      start_date: [pendingStartDate],
      end_date: [pendingEndDate],
      ...output,
    };
    return result_output;
  }

  useEffect(() => {
    setCurrentPage(dashboard?.currentPage ?? 0);
  }, [dashboard?.currentPage]);

  useEffect(() => {
    setPageSize(dashboard?.pageSize ?? 10);
  }, [dashboard?.pageSize]);

  const getKpiMetricGrid = useCallback(
    async (data_type: number, params: SelectedFiltersList) => {
      try {
        dispatch(addLoadingAction('dashboardKpiGrid'));
        const result = await fetchKpiMetricGrid(data_type, params);

        if (result) {
          setGridData(result);
          dispatch(removeLoadingAction('dashboardKpiGrid'));
        }
      } catch (error) {
        console.error('Failed to fetch dashboardKpiGrid:', error);
      } finally {
        // Stop loading
        dispatch(removeLoadingAction('dashboardKpiGrid'));
      }
    },
    [dispatch, fetchKpiMetricGrid]
  );

  const getKpiMetricGraph = useCallback(
    async (data_type: Metric, params: SelectedFiltersList) => {
      try {
        const data_id = data_type?.id === undefined ? 1 : data_type.id;
        dispatch(addLoadingAction('dashboardKpiGraph'));
        const result = await fetchKpiMetricGraph(data_id, params);
        if (result) {
          setGraphData(result);
          if (data_type.page_title === KpiMetricTableType.WORKFLOW) {
            setBarChartData(transformPendingData(result?.orders?.pending_data, data_id));
          } else if (data_type.page_title === KpiMetricTableType.ACCEPTANCE) {
            setBarChartData(transformPendingData(result?.orders?.acceptance_data, data_id));
          } else if (data_type.page_title === KpiMetricTableType.PERFORMANCE) {
            setBarChartData(transformPendingData(result?.orders?.pending_data, data_id));
          } else if (data_type.page_title === KpiMetricTableType.COVER_RATIO) {
            setBarChartData(transformPendingData(result?.orders, data_id));
          } else if (data_type.page_title === KpiMetricTableType.FINANCIAL_KPI) {
            setBarChartData(result?.orders);
          }
          // const barData = data_type === 1 ? result?.orders?.pending_data : result?.orders?.acceptance_data;
          dispatch(removeLoadingAction('dashboardKpiGraph'));
        }
      } catch (error) {
        console.error('Failed to fetch dashboardKpiGraph:', error);
      } finally {
        // Stop loading
        dispatch(removeLoadingAction('dashboardKpiGraph'));
      }
    },
    [dispatch, fetchKpiMetricGraph]
  );

  const fetchDonutGrid = async () => {
    // 1. Set loading state before try block execution
    if (metric?.id && targetIds.includes(metric.id)) {
      dispatch(addLoadingAction('dashboardKpiGrid'));
    }

    try {
      const params = await formatSelected(filter.selected);

      // Add currency for page 4 (Financial KPI)
      if (metric?.id && targetIds.includes(metric.id)) {
        params.currency = selectedCurrency;
      }

      // Derive sort params from dashboard state
      let sort_field: string | undefined;
      let sort_by: string | undefined;
      if (dashboard?.sortKpiMetricGridData && dashboard.sortKpiMetricGridData.length > 0) {
        const { field, sort } = dashboard.sortKpiMetricGridData[0];
        sort_field = field;
        sort_by = sort?.toUpperCase();
      }

      // Prefer dashboard-controlled pagination
      const page = dashboard?.currentPage ?? currentPage;
      const size = dashboard?.pageSize ?? pageSize;

      const searchParams: any = sort_field ? { size, page, sort_field, sort_by, ...params } : { size, page, ...params };

      // Trigger API call if dates are valid
      if (params?.start_date && params?.end_date) {
        await getKpiMetricGrid(metric?.id || initialMetricId, searchParams);
      }
    } catch (error) {
      // 2. Handle API or formatting errors
      console.error('Failed to fetch donut grid data:', error);
      // Protip: Dispatch a global error toast action here if you have one
    } finally {
      // 3. Always remove loading state, even if the API fails
      if (metric?.id && targetIds.includes(metric.id)) {
        dispatch(removeLoadingAction('dashboardKpiGrid'));
      }
    }
  };

  useEffect(() => {
    // If the change was triggered by fetchKpiFilter, skip execution and reset the flag
    if (isInitialKpiFetch.current) {
      isInitialKpiFetch.current = false;
      return;
    }

    if (metric !== null && window.location.pathname.includes('dashboard')) {
      fetchDonutGrid();
      if (metric?.id && metric?.id < 6) fetchDonutGraph();
    }
  }, [metric]);

  useEffect(() => {
    if (isFirstLoadEffect1.current) {
      isFirstLoadEffect1.current = false;
      return;
    }
    if (metric !== null && window.location.pathname.includes('dashboard')) {
      fetchDonutGrid();
    }
  }, [dashboard?.currentPage, dashboard?.pageSize]);

  useEffect(() => {
    if (dashboard?.sortKpiMetricGridData && dashboard.sortKpiMetricGridData.length > 0) {
      fetchDonutGrid();
    }
  }, [dashboard?.sortKpiMetricGridData]);

  const fetchDonutGraph = async () => {
    const params = await formatSelected(filter.selected);
    // Add currency for page 4 (Financial KPI)
    if (metric?.id && targetIds.includes(metric.id)) {
      params.currency = selectedCurrency;
    }
    if (params?.start_date && params?.end_date) {
      await getKpiMetricGraph(metric, params);
    }
  };

  const { donutDataConsultant, donutDataDM, donutDataClient, coverRatioData, otpCustomerData, otpBMData } =
    React.useMemo(() => {
      const ordersForAggregation = graphData?.orders as any;

      // 1. If Metric is 4, we use 'order' (singular)
      if (metric?.page_title === KpiMetricTableType.COVER_RATIO) {
        const coverRatio = aggregateDonutData(ordersForAggregation || {}, metric?.id || initialMetricId);
        return {
          donutDataConsultant: [], // Empty because metric 4 doesn't use these
          donutDataDM: [],
          donutDataClient: [],
          coverRatioData: coverRatio?.missionByCustomer || [],
          otpCustomerData: coverRatio?.missionByBM || [],
          otpBMData: coverRatio?.missionData || [],
        };
      }

      // 2. Otherwise (Metric 1, 2, or 3), we use 'orders' (plural) and aggregate
      const aggregated = aggregateDonutData(ordersForAggregation || {}, metric?.id || initialMetricId);

      return {
        donutDataConsultant: aggregated.donutDataConsultant || [],
        donutDataDM: aggregated.donutDataDM || [],
        donutDataClient: aggregated.donutDataClient || [],
        coverRatioData: [], // Empty because metric 1/2 don't use these
        otpCustomerData: [],
        otpBMData: [],
      };
    }, [graphData, metric?.id]);

  const handleDownloadXlsx = useCallback(async () => {
    try {
      const params = await formatSelected(filter.selected);
      // Add currency for page 4 (Financial KPI)
      if (metric?.id && targetIds.includes(metric.id)) {
        params.currency = selectedCurrency;
      }

      dispatch(addLoadingAction('dashboardKpiExportXlsx'));

      const { data, filename } = await downloadKpiGridExcel(metric?.id || initialMetricId, params);

      if (!data) throw new Error('No XLSX data received');

      const a = document.createElement('a');
      a.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${data}`;
      a.download = `${filename}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      console.error('Download XLSX failed:', e);
    } finally {
      dispatch(removeLoadingAction('dashboardKpiExportXlsx'));
    }
  }, [filter.selected, metric?.id, dispatch, downloadKpiGridExcel]);

  // Now you can use donutDataConsultant, donutDataDM, and donutDataClient in your component render. '#c0c0c0',

  const COLORS_CONSULTANT = ['#61D2A4', '#FF6040'];
  const COLORS_DM = ['#FF6040', '#61D2A4', '#FFC107'];
  const COLORS_CLIENT = ['#FF6040', '#61D2A4', '#FFC107'];
  const COLORS_ACCEPTANCE_CLIENT = ['#61D2A4', '#3B82F6', '#FF6040', '#FFD200'];
  const RATING_COLORS = ['#FF6040', '#808080', '#FF9100', '#FFD200', '#3C83F0', '#60C39B', '#E600E6'];

  const isInitialLoading = loadings?.dashboardSuperFilter || metric === null;
  const isGraphLoading = Boolean(loadings?.dashboardKpiGraph) || Boolean(loadings?.dashboardKpiGrid);
  if (isInitialLoading || isGraphLoading) {
    return (
      <Box sx={{ height: '75vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader />
      </Box>
    );
  }

  return (
    <Grid container spacing={3} sx={{ flexGrow: 1, pt: 1, height: 'calc(100vh - 64px)' }}>
      {/* ----------- TOP FILTER ROW (Matches Screenshot) ----------- */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          {/* Left Side Container */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel id="metrics-select-label">{t('Metrics')}</InputLabel>
              <Select
                labelId="metrics-select-label"
                id="metrics-select"
                value={metric?.id || ''}
                label="Metrics"
                onChange={(e) => {
                  const selected = metricsList.find((m) => m.id === e.target.value);
                  if (!selected) return;

                  // Clear common visual states immediately
                  setBarChartData([]);
                  setGridData([]);

                  // Destructure ids for cleaner condition checks
                  const { id: selectedId } = selected;
                  const currentMetricId = metric?.id;

                  const isDeliveryPage = (id?: number) => id !== undefined && DELIVERY_METRIC_IDS.includes(id);
                  const isFinancialPage = (id?: number) => id !== undefined && targetIds.includes(id);

                  // Navigation rules (EDD-436):
                  // - Any page -> Cover Ratio (4): reset date range only, keep filters
                  // - Delivery (1,2,3) <-> Delivery: maintain
                  // - Non-delivery -> Delivery: reset everything
                  // - Financial (5,6,7) <-> Financial: maintain
                  // - Delivery/Cover Ratio -> Financial: clear a subset of filters
                  let action: 'maintain' | 'resetAll' | 'clear' | 'resetDateOnly' = 'maintain';
                  if (selectedId === COVER_RATIO_METRIC_ID) {
                    action = 'resetDateOnly';
                  } else if (isDeliveryPage(selectedId)) {
                    action = isDeliveryPage(currentMetricId) ? 'maintain' : 'resetAll';
                  } else if (isFinancialPage(selectedId)) {
                    action = isFinancialPage(currentMetricId) ? 'maintain' : 'clear';
                  }

                  if (action === 'resetDateOnly') {
                    isInitialKpiFetch.current = true;
                    setPendingStartDate(DEFAULT_START_DATE);
                    setPendingEndDate(DEFAULT_END_DATE);
                    setForceUpdate((prev) => prev + 1);
                  } else if (action === 'resetAll') {
                    isInitialKpiFetch.current = true;
                    dispatch(sortKpimetricGridAction([]));
                    dispatch(
                      selectFilterRemoveAction({
                        order_status: '',
                        affair_otp: '',
                        customer_id: '',
                        order_id: '',
                        deliverable_status: '',
                        client_id: '',
                        consultant_id: '',
                        delivery_manager: '',
                        country: '',
                        bm_id: '',
                      })
                    );
                    setFilterValues(null);
                    setPendingStartDate(DEFAULT_START_DATE);
                    setPendingEndDate(DEFAULT_END_DATE);
                    setForceUpdate((prev) => prev + 1);
                  } else if (action === 'clear') {
                    dispatch(sortKpimetricGridAction([]));
                    dispatch(
                      selectFilterRemoveAction({
                        bm_id: '',
                        country: '',
                        affair_otp: '',
                        customer_id: '',
                      })
                    );
                    setFilterValues((prev) => ({
                      ...prev,
                      bm_id: 'ALL',
                      country: 'ALL',
                      affair_otp: 'ALL',
                      customer_id: 'ALL',
                    }));
                    setPendingStartDate(DEFAULT_START_DATE);
                    setPendingEndDate(DEFAULT_END_DATE);
                    setForceUpdate((prev) => prev + 1);
                  } else {
                    isInitialKpiFetch.current = false;
                  }
                  setMetric(selected);
                }}
                disabled={!metricsList || metricsList.length === 0}
              >
                {[
                  { label: t('Delivery'), indices: [0, 1, 2] },
                  { label: t('Coverage & Data Completeness'), indices: [3] },
                  { label: t('Financial'), indices: [4, 5, 6] },
                ].flatMap(({ label, indices }) => {
                  const items = indices
                    .map((i) => metricPage[i])
                    .filter(Boolean)
                    .map((mp) => metricsList.find((m) => m.id === mp.id))
                    .filter((m): m is Metric => m !== undefined);
                  if (items.length === 0) return [];
                  return [
                    <ListSubheader key={label} sx={{ fontSize: 16, lineHeight: '32px', color: 'primary.main' }}>
                      {label}
                    </ListSubheader>,
                    ...items.map((m) => (
                      <MenuItem key={m.id} value={m.id} sx={{ pl: 4 }}>
                        {m.page_title}
                      </MenuItem>
                    )),
                  ];
                })}
              </Select>
            </FormControl>
            {metric?.id && targetIds.includes(metric.id) && (
              <FormControl sx={{ minWidth: 250 }}>
                <InputLabel id="currency-select-label">{t('Currency')}</InputLabel>
                <Select
                  labelId="currency-select-label"
                  id="currency-select"
                  value={selectedCurrency} // Bound to string state ("Euro")
                  label="Currency"
                  onChange={(e) => {
                    const selectedName = e.target.value;
                    setSelectedCurrency(selectedName); // Safely saves "Euro", "Dollar", etc.
                    console.log('Selected currency Name for API:', selectedName);
                    handleDateChange();
                  }}
                >
                  {currencies?.map((c) => (
                    <MenuItem key={c.id} value={c.name}>
                      {c.name} ({c.symbol})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          {/* Right Side Button */}
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#1A73E8',
              color: '#fff',
              borderRadius: '12px',
              px: 3,
              py: 1,
              textTransform: 'none',
              fontSize: '18px',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              boxShadow: '0px 2px 6px rgba(0,0,0,0.15)',
              '&:hover': {
                backgroundColor: '#1669C1',
              },
            }}
            onClick={() => setShowFilterPanel((p) => !p)}
          >
            Filter
          </Button>
        </Box>
      </Grid>

      {/* ----------- MAIN CONTENT ----------- */}
      <Grid item xs={12} lg={showFilterPanel ? 9.25 : 12}>
        <React.Suspense fallback={<Loader />}>
          {(() => {
            // Helper variable to make the switch condition shorter and cleaner
            const showDownload = Boolean((gridData as any)?.orders?.totalItems);

            switch (metric?.id) {
              case metricPage[0].id:
                return (
                  <KpiMetricsWorkflow
                    donutDataConsultant={donutDataConsultant}
                    donutDataDM={donutDataDM}
                    donutDataClient={donutDataClient}
                    COLORS_CONSULTANT={COLORS_CONSULTANT}
                    COLORS_DM={COLORS_DM}
                    COLORS_CLIENT={COLORS_CLIENT}
                    barChartData={barChartData}
                    metric={metric}
                    data_table={gridData}
                    onDownloadXlsx={handleDownloadXlsx}
                    showDownload={showDownload}
                  />
                );

              case metricPage[1].id:
                return (
                  <KpiMetricsAcceptance
                    donutDataClient={donutDataClient}
                    COLORS_CONSULTANT={COLORS_CONSULTANT}
                    COLORS_DM={COLORS_DM}
                    COLORS_CLIENT={COLORS_ACCEPTANCE_CLIENT}
                    barChartData={barChartData}
                    metric={metric}
                    data_table={gridData}
                    onDownloadXlsx={handleDownloadXlsx}
                    showDownload={showDownload}
                  />
                );

              case metricPage[2].id:
                return (
                  <KpiMetricsPerformance
                    donutDataClient={donutDataClient}
                    COLORS_CONSULTANT={COLORS_CONSULTANT}
                    COLORS_DM={COLORS_DM}
                    COLORS_CLIENT={RATING_COLORS}
                    barChartData={barChartData}
                    metric={metric}
                    data_table={gridData}
                    onDownloadXlsx={handleDownloadXlsx}
                    showDownload={showDownload}
                  />
                );

              case metricPage[3].id:
                return (
                  <KpiMetricsCoverRatio
                    barChartData={barChartData?.[0]}
                    metric={metric}
                    data_table={gridData}
                    onDownloadXlsx={handleDownloadXlsx}
                    showDownload={showDownload}
                  />
                );

              default:
                return null; // Return null if no metric ID matches to prevent layout breaks
            }
          })()}
        </React.Suspense>
      </Grid>

      {/* ----------- FILTER PANEL ----------- */}
      {showFilterPanel && (
        <Grid item xs={12} lg={2.5}>
          <FilterPanel
            metricId={metric?.id}
            pendingStartDate={pendingStartDate}
            pendingEndDate={pendingEndDate}
            setPendingStartDate={setPendingStartDate}
            setPendingEndDate={setPendingEndDate}
            filterValues={filterValues}
            setFilterValues={setFilterValues}
            handleDateChange={handleDateChange}
            setForceUpdate={setForceUpdate}
            displaySelectedCountry={displaySelectedCountry}
            setDisplaySelectedCountry={setDisplaySelectedCountry}
            displaySelectedStatus={displaySelectedStatus}
            setDisplaySelectedStatus={setDisplaySelectedStatus}
            metricPage={metricPage}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default KpiMetricsTab;
